import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { BadgeCheck, Check, Pencil, Plus, Tags, Trash2, X } from 'lucide-react';
import type { FeatureDefinition } from '@genealogy-app/shared';
import {
  auswirkungFuerTarif,
  createTarif,
  deleteTarif,
  fetchTarife,
  setTarifVorgabe,
  updateTarif,
  type Tarif,
  type TarifEingabe,
} from '@/api/plans.api';
import { useConfirm } from '@/components/common/ConfirmDialog';
import { bytes, geld, zahl } from '@/components/format';
import { Tarifwirkung } from '@/components/Tarifwirkung';

// ─── Tarife ──────────────────────────────────────────────────────────────────
//
// Ein Tarif bündelt vier Grenzen: wie viele Stammbäume ein Konto anlegen darf,
// wie viel Platz es insgesamt bekommt, wie groß ein einzelner Stammbaum werden
// darf, und welche Funktionen es überhaupt gibt.
//
// Die beiden Grenzen zählen bewusst verschieden:
//
//   SPEICHER  gilt für ALLE Bäume eines Kontos zusammen. Bis August 2026 galt
//             er je Baum; das war schwer zu überblicken – wer zwei Bäume hatte,
//             musste zwei Zahlen im Kopf behalten – und stimmte nicht mit dem
//             überein, was auf der Platte lag.
//   PERSONEN  gilt je Stammbaum. Ein Stammbaum ist eine Familie, und die
//             Grenze sagt, wie groß eine Familie werden darf. Wie viele
//             Familien jemand nebeneinander erforscht, regelt „Stammbäume".
//
// Maßgeblich ist der Tarif des BESITZERS. Ein Mitglied bringt seinen eigenen
// Tarif nicht in fremde Bäume mit.

const GB = 1024 * 1024 * 1024;

/** Bytes → GB als Text fürs Eingabefeld. Leer heißt unbegrenzt. */
function alsGb(b: number | null): string {
  if (b === null) return '';
  return String(Math.round((b / GB) * 100) / 100);
}

function ausGb(text: string): number | null {
  // Komma zulassen: das Feld zeigt deutsche Zahlen an, und wer „0,5" tippt,
  // meint ein halbes Gigabyte – nicht „unbegrenzt".
  const t = text.trim().replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * GB);
}

/** Cent → Euro als Text fürs Eingabefeld. Leer heißt: kein Preis hinterlegt. */
function alsEuro(cents: number | null): string {
  if (cents === null) return '';
  return String(cents / 100);
}

function ausEuro(text: string): number | null {
  // Wie beim Speicher: Komma zulassen, „12,50" meint zwölf Euro fünfzig.
  const t = text.trim().replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

const INTERVALL_TEXT: Record<string, string> = {
  month: 'je Monat',
  year: 'je Jahr',
  once: 'einmalig',
};

interface FormularStand {
  name: string;
  description: string;
  preisEuro: string;
  interval: string;
  imShop: boolean;
  hervorgehoben: boolean;
  maxTrees: string;
  speicherGb: string;
  maxPersons: string;
  features: Set<string>;
}

function leeresFormular(): FormularStand {
  return {
    name: '',
    description: '',
    preisEuro: '',
    interval: 'month',
    imShop: false,
    hervorgehoben: false,
    maxTrees: '3',
    speicherGb: '',
    maxPersons: '',
    features: new Set(),
  };
}

function ausTarif(t: Tarif): FormularStand {
  return {
    name: t.name,
    description: t.description ?? '',
    preisEuro: alsEuro(t.priceCents),
    interval: t.interval ?? 'month',
    imShop: t.imShop,
    hervorgehoben: t.hervorgehoben,
    maxTrees: String(t.maxTrees),
    speicherGb: alsGb(t.maxStorageBytes),
    maxPersons: t.maxPersons === null ? '' : String(t.maxPersons),
    features: new Set(t.features),
  };
}

export function TarifePage() {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'plans'],
    queryFn: fetchTarife,
  });

  /** null = Dialog zu, 'neu' = anlegen, sonst die ID des Tarifs. */
  const [offen, setOffen] = useState<string | null>(null);
  const [formular, setFormular] = useState<FormularStand>(leeresFormular);

  const aktualisieren = () => queryClient.invalidateQueries({ queryKey: ['admin', 'plans'] });

  const speichern = useMutation({
    mutationFn: async (): Promise<void> => {
      const dto: TarifEingabe = {
        name: formular.name.trim(),
        description: formular.description.trim() || null,
        maxTrees: Number(formular.maxTrees),
        maxStorageBytes: ausGb(formular.speicherGb),
        maxPersons: formular.maxPersons.trim() === '' ? null : Number(formular.maxPersons),
        features: [...formular.features],
        priceCents: ausEuro(formular.preisEuro),
        // Ein Intervall ohne Preis wäre eine Angabe ohne Gegenstand.
        interval: ausEuro(formular.preisEuro) === null ? null : formular.interval,
        imShop: formular.imShop,
        hervorgehoben: formular.hervorgehoben,
      };
      if (offen === 'neu') await createTarif(dto);
      else if (offen) await updateTarif(offen, dto);
    },
    onSuccess: async () => {
      await aktualisieren();
      toast.success(offen === 'neu' ? 'Tarif angelegt.' : 'Tarif gespeichert.');
      setOffen(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const vorgabe = useMutation({
    mutationFn: setTarifVorgabe,
    onSuccess: async () => {
      await aktualisieren();
      toast.success('Vorgabe geändert.');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const entfernen = useMutation({
    mutationFn: deleteTarif,
    onSuccess: async () => {
      await aktualisieren();
      toast.success('Tarif gelöscht.');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Was die getippten Grenzen für die Konten in DIESEM Tarif bedeuten.
  //
  // Der gefährlichere der beiden Fälle: eine gesenkte Grenze trifft nicht ein
  // Konto, sondern jedes, das den Tarif hält – und ohne diese Zeile sieht es
  // niemand. Nur beim Bearbeiten, nicht beim Anlegen: ein neuer Tarif hat noch
  // keine Konten.
  const entwurf = useMemo(
    () => ({
      maxTrees: Number(formular.maxTrees) || 0,
      maxStorageBytes: ausGb(formular.speicherGb),
      maxPersons: formular.maxPersons.trim() === '' ? null : Number(formular.maxPersons),
      features: [...formular.features],
    }),
    [formular.maxTrees, formular.speicherGb, formular.maxPersons, formular.features],
  );

  // Erst nach einer kurzen Pause fragen. Wer „1500" tippt, meint nicht
  // nacheinander 1, 15 und 150 – und jede dieser Zwischenzahlen kostet eine
  // Runde über alle Konten des Tarifs.
  const [ruhend, setRuhend] = useState(entwurf);
  useEffect(() => {
    const t = setTimeout(() => setRuhend(entwurf), 400);
    return () => clearTimeout(t);
  }, [entwurf]);

  const { data: wirkung, isFetching: wirkungLaeuft } = useQuery({
    queryKey: ['admin', 'plans', 'auswirkung', offen, ruhend],
    queryFn: () => auswirkungFuerTarif(offen!, ruhend),
    enabled: !!offen && offen !== 'neu',
    staleTime: 0,
  });

  const gruppen = useMemo(() => {
    const m = new Map<string, FeatureDefinition[]>();
    for (const f of data?.features ?? []) {
      const liste = m.get(f.group) ?? [];
      liste.push(f);
      m.set(f.group, liste);
    }
    return [...m.entries()];
  }, [data?.features]);

  const beschriftung = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of data?.features ?? []) m.set(f.key, f.label);
    return m;
  }, [data?.features]);

  const loeschen = async (t: Tarif) => {
    const ok = await confirm({
      title: `Tarif „${t.name}" löschen`,
      message: 'Der Tarif verschwindet aus der Auswahl. Bestehende Konten bleiben unberührt.',
      confirmLabel: 'Löschen',
      variant: 'danger',
    });
    if (ok) entfernen.mutate(t.id);
  };

  if (isLoading) {
    return <div className="p-4 text-sm text-slate-500 sm:p-6">Tarife werden geladen…</div>;
  }

  if (isError || !data) {
    return (
      <div className="p-4 sm:p-6">
        <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error instanceof Error ? error.message : 'Tarife konnten nicht geladen werden.'}
        </p>
      </div>
    );
  }

  const nameFehlt = formular.name.trim() === '';
  const baeumeUngueltig =
    formular.maxTrees.trim() === '' ||
    !Number.isInteger(Number(formular.maxTrees)) ||
    Number(formular.maxTrees) < 0;
  const speicherUngueltig =
    formular.speicherGb.trim() !== '' && ausGb(formular.speicherGb) === null;
  const preisUngueltig =
    formular.preisEuro.trim() !== '' && ausEuro(formular.preisEuro) === null;
  const personenUngueltig =
    formular.maxPersons.trim() !== '' &&
    (!Number.isInteger(Number(formular.maxPersons)) || Number(formular.maxPersons) < 1);

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-900 sm:text-2xl">
            <Tags className="h-5 w-5 text-slate-400" aria-hidden="true" />
            Tarife
          </h1>
          <p className="mt-0.5 text-sm text-slate-500">
            Grenzen und Funktionsumfang. Für einen Stammbaum gilt immer der Tarif
            seines Besitzers.
          </p>
        </div>
        <button
          onClick={() => {
            setFormular(leeresFormular());
            setOffen('neu');
          }}
          className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          Tarif anlegen
        </button>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {data.data.map((t) => (
          <article
            key={t.id}
            className={`flex flex-col rounded-xl border bg-white p-5 ${
              t.isDefault ? 'border-blue-300 ring-1 ring-blue-100' : 'border-slate-200'
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <h2 className="text-base font-semibold text-slate-900">{t.name}</h2>
              {t.isDefault && (
                <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                  <BadgeCheck className="h-3.5 w-3.5" aria-hidden="true" />
                  Vorgabe
                </span>
              )}
            </div>
            {t.description && (
              <p className="mt-1 text-sm leading-relaxed text-slate-600">{t.description}</p>
            )}

            <p className="mt-2 flex flex-wrap items-baseline gap-x-1.5 gap-y-1">
              <span className="text-lg font-semibold text-slate-900">
                {t.priceCents === null ? 'kein Preis' : geld(t.priceCents, t.currency)}
              </span>
              {t.priceCents !== null && t.interval && (
                <span className="text-xs text-slate-500">{INTERVALL_TEXT[t.interval]}</span>
              )}
              {t.imShop ? (
                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                  im Shop
                </span>
              ) : (
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                  nicht im Shop
                </span>
              )}
              {t.hervorgehoben && (
                <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                  hervorgehoben
                </span>
              )}
            </p>

            <dl className="mt-3 space-y-1 text-sm">
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">Stammbäume</dt>
                <dd className="font-medium text-slate-800">{zahl(t.maxTrees)}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">Speicher gesamt</dt>
                <dd className="font-medium text-slate-800">
                  {t.maxStorageBytes === null ? 'unbegrenzt' : bytes(t.maxStorageBytes)}
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">Personen je Baum</dt>
                <dd className="font-medium text-slate-800">
                  {t.maxPersons === null ? 'unbegrenzt' : zahl(t.maxPersons)}
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">Konten</dt>
                <dd className="font-medium text-slate-800">{zahl(t.userCount ?? 0)}</dd>
              </div>
            </dl>

            <div className="mt-3 flex flex-wrap gap-1">
              {t.features.length === 0 ? (
                <span className="text-xs italic text-slate-400">keine Funktionen</span>
              ) : (
                t.features.map((f) => (
                  <span
                    key={f}
                    className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600"
                  >
                    {beschriftung.get(f) ?? f}
                  </span>
                ))
              )}
            </div>

            <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
              <button
                onClick={() => {
                  setFormular(ausTarif(t));
                  setOffen(t.id);
                }}
                className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
              >
                <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                Bearbeiten
              </button>
              {!t.isDefault && (
                <>
                  <button
                    onClick={() => vorgabe.mutate(t.id)}
                    disabled={vorgabe.isPending}
                    className="rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    Zur Vorgabe machen
                  </button>
                  <button
                    onClick={() => loeschen(t)}
                    disabled={entfernen.isPending}
                    className="inline-flex items-center gap-1 rounded-md border border-red-200 px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                    Löschen
                  </button>
                </>
              )}
            </div>
          </article>
        ))}
      </div>

      {data.data.length === 0 && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          Es gibt noch keinen Tarif. Solange keiner existiert, gelten für alle
          Konten keinerlei Grenzen – legen Sie mindestens einen an und machen Sie
          ihn zur Vorgabe.
        </p>
      )}

      {/* ─── Dialog ──────────────────────────────────────────────────────── */}
      {offen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 p-0 sm:items-center sm:p-4">
          <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-xl bg-white shadow-xl sm:rounded-xl">
            <header className="flex shrink-0 items-center justify-between border-b border-slate-200 px-5 py-3">
              <h2 className="text-base font-semibold text-slate-900">
                {offen === 'neu' ? 'Tarif anlegen' : 'Tarif bearbeiten'}
              </h2>
              <button
                onClick={() => setOffen(null)}
                aria-label="Schließen"
                className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </header>

            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label htmlFor="tarif-name" className="mb-1 block text-xs font-medium text-slate-700">
                    Name
                  </label>
                  <input
                    id="tarif-name"
                    value={formular.name}
                    onChange={(e) => setFormular({ ...formular, name: e.target.value })}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="tarif-text" className="mb-1 block text-xs font-medium text-slate-700">
                    Beschreibung
                  </label>
                  <textarea
                    id="tarif-text"
                    rows={2}
                    value={formular.description}
                    onChange={(e) => setFormular({ ...formular, description: e.target.value })}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>
                {/* ─── Verkauf ───
                    Getrennt von den Grenzen darunter: was ein Tarif KANN, ist
                    eine andere Frage als was er KOSTET. */}
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 sm:col-span-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Verkauf
                  </p>
                  <div className="mt-2 grid gap-3 sm:grid-cols-2">
                    <div>
                      <label
                        htmlFor="tarif-preis"
                        className="mb-1 block text-xs font-medium text-slate-700"
                      >
                        Preis in Euro
                      </label>
                      <input
                        id="tarif-preis"
                        inputMode="decimal"
                        value={formular.preisEuro}
                        onChange={(e) => setFormular({ ...formular, preisEuro: e.target.value })}
                        placeholder="15,00"
                        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm tabular-nums focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                      />
                      <p className="mt-1 text-xs text-slate-400">
                        Leer = kein Preis. 0 = kostenlos; so ein Tarif wird beim
                        Bestellen sofort freigeschaltet.
                      </p>
                    </div>
                    <div>
                      <label
                        htmlFor="tarif-intervall"
                        className="mb-1 block text-xs font-medium text-slate-700"
                      >
                        Abrechnung
                      </label>
                      <select
                        id="tarif-intervall"
                        value={formular.interval}
                        disabled={formular.preisEuro.trim() === ''}
                        onChange={(e) => setFormular({ ...formular, interval: e.target.value })}
                        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100 disabled:text-slate-400"
                      >
                        <option value="month">je Monat</option>
                        <option value="year">je Jahr</option>
                        <option value="once">einmalig</option>
                      </select>
                    </div>
                  </div>

                  <label className="mt-3 flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={formular.imShop}
                      onChange={(e) => setFormular({ ...formular, imShop: e.target.checked })}
                      className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600"
                    />
                    <span className="text-sm text-slate-700">
                      Im Shop anbieten
                      <span className="block text-xs text-slate-400">
                        Ohne Haken bleibt der Tarif intern – Sie können ihn weiter von
                        Hand zuweisen, nur bestellen kann ihn niemand.
                      </span>
                    </span>
                  </label>

                  <label className="mt-2 flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={formular.hervorgehoben}
                      disabled={!formular.imShop}
                      onChange={(e) =>
                        setFormular({ ...formular, hervorgehoben: e.target.checked })
                      }
                      className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 disabled:opacity-40"
                    />
                    <span className="text-sm text-slate-700">
                      Hervorgehoben darstellen
                      <span className="block text-xs text-slate-400">
                        Die Empfehlung der Preistafel. Sinnvoll für genau einen Tarif.
                      </span>
                    </span>
                  </label>
                </div>

                <div>
                  <label htmlFor="tarif-baeume" className="mb-1 block text-xs font-medium text-slate-700">
                    Stammbäume je Konto
                  </label>
                  <input
                    id="tarif-baeume"
                    type="number"
                    min={0}
                    value={formular.maxTrees}
                    onChange={(e) => setFormular({ ...formular, maxTrees: e.target.value })}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                  <p className="mt-1 text-xs text-slate-400">
                    0 = das Konto legt keine eigenen an.
                  </p>
                </div>
                <div>
                  <label htmlFor="tarif-speicher" className="mb-1 block text-xs font-medium text-slate-700">
                    Speicher gesamt (GB)
                  </label>
                  <input
                    id="tarif-speicher"
                    type="number"
                    min={0}
                    // „any" statt einer festen Stufe: mit step=0.5 hielt der
                    // Browser 0,25 GB für ungültig, obwohl der Wert genau so
                    // aus dem gespeicherten Tarif kam.
                    step="any"
                    placeholder="unbegrenzt"
                    value={formular.speicherGb}
                    onChange={(e) => setFormular({ ...formular, speicherGb: e.target.value })}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                  <p className="mt-1 text-xs text-slate-400">
                    Für alle Stammbäume des Kontos zusammen. Leer = unbegrenzt.
                  </p>
                </div>

                <div>
                  <label htmlFor="tarif-personen" className="mb-1 block text-xs font-medium text-slate-700">
                    Personen je Stammbaum
                  </label>
                  <input
                    id="tarif-personen"
                    type="number"
                    min={1}
                    placeholder="unbegrenzt"
                    value={formular.maxPersons}
                    onChange={(e) => setFormular({ ...formular, maxPersons: e.target.value })}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                  <p className="mt-1 text-xs text-slate-400">
                    Gilt je Baum, nicht je Konto: ein Stammbaum ist eine Familie.
                    Leer = unbegrenzt.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-slate-900">Funktionen</h3>
                <p className="mt-0.5 text-xs text-slate-500">
                  Was in Stammbäumen dieses Tarifs zur Verfügung steht. Eine
                  ausgeschaltete Funktion ist auch für Besitzer gesperrt.
                </p>

                <div className="mt-3 space-y-4">
                  {gruppen.map(([gruppe, felder]) => (
                    <div key={gruppe}>
                      <p className="mb-1.5 text-xs font-medium uppercase tracking-wider text-slate-400">
                        {gruppe}
                      </p>
                      <div className="space-y-1.5">
                        {felder.map((f) => {
                          const an = formular.features.has(f.key);
                          return (
                            <label
                              key={f.key}
                              className="flex cursor-pointer items-start gap-2.5 rounded-md border border-slate-200 p-2.5 hover:bg-slate-50"
                            >
                              <input
                                type="checkbox"
                                checked={an}
                                onChange={() => {
                                  const neu = new Set(formular.features);
                                  if (an) neu.delete(f.key);
                                  else neu.add(f.key);
                                  setFormular({ ...formular, features: neu });
                                }}
                                className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-400"
                              />
                              <span className="min-w-0">
                                <span className="block text-sm font-medium text-slate-800">
                                  {f.label}
                                </span>
                                <span className="block text-xs leading-relaxed text-slate-500">
                                  {f.description}
                                </span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Vor den Knöpfen, nicht danach: wer schon auf „Speichern" zielt,
                soll den Hinweis noch im Blick haben. */}
            {offen !== 'neu' && (
              <div className="shrink-0 px-5 pb-1">
                <Tarifwirkung auswirkung={wirkung} laeuft={wirkungLaeuft} mehrereKonten />
              </div>
            )}

            <footer className="flex shrink-0 justify-end gap-2 border-t border-slate-200 px-5 py-3">
              <button
                onClick={() => setOffen(null)}
                className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Abbrechen
              </button>
              <button
                onClick={() => speichern.mutate()}
                disabled={
                  speichern.isPending ||
                  nameFehlt ||
                  baeumeUngueltig ||
                  speicherUngueltig ||
                  personenUngueltig ||
                  preisUngueltig
                }
                className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                <Check className="h-4 w-4" aria-hidden="true" />
                {speichern.isPending ? 'Wird gespeichert…' : 'Speichern'}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

export default TarifePage;
